import request from 'superagent';
import moment from 'moment';
import jwt from 'jsonwebtoken';
import queryString from 'query-string';
import {
  get,
} from 'lodash';
import {
  store,
} from '../lib/styleguide/Wrapper';
import {
  doAuthenticateUser,
} from '../actions/Login/LoginActions';
import {
  setPanel,
} from '../actions/Global/GlobalActions';
import {
  getLocal,
  setLocal,
  removeLocal,
} from './StorageUtil';
import pkg from '../../../../package.json';

const appEnv = process.env.APP_ENV;
const local = appEnv === 'local';
const development = appEnv === 'development';
const clientHeader = local || development
  ? `web/Dev V${pkg.version}`
  : `web/${pkg.version}`;

/**
 * Check if the user session is valid
 */
function isValidSession() {
  const idToken = getLocal('app:user:idToken');
  const idPayload = jwt.decode(idToken);

  if (idPayload) {
    const idExp = moment.unix(idPayload.exp);
    const now = moment();

    return idExp.isAfter(now);
  }

  return false;
}

/**
 * Authenticate the user
 */
export async function authenticate(authData) {
  return new Promise((resolve, reject) => {
    request
      .post(`${process.env.VAPI_BASE_URL}/authentication/authenticate`)
      .send(authData)
      .set('Accept', 'application/json')
      .set('VAPI-Client', clientHeader)
      .end((err, res) => {
        const errorMessage = get(res, 'body.@@errorMessage');

        if (errorMessage) {
          reject(new Error(errorMessage));
        } else {
          resolve(res.body);
        }
      });
  });
}

/**
 * Log the user out of the application
 */
export function logout() {
  // Clear the cookies
  removeLocal('app:user:idToken');
  removeLocal('app:user:accessToken');
  removeLocal('app:user:refreshToken');
  removeLocal('app:media:params');
  removeLocal('CloudFront-Key-Pair-Id');
  removeLocal('CloudFront-Policy');
  removeLocal('CloudFront-Signature');

  // Remove deprecated local storage items
  removeLocal('cookiesAccepted');
  removeLocal('cookiesAcceptedDate');

  // force the user to logout
  store.dispatch(doAuthenticateUser(false));

  // close the sidepanel (and secondary/tertiary panels)
  store.dispatch(setPanel({
    show: false,
    extraPanel: {
      show: false,
    },
    thirdPanel: {
      show: false,
    },
    fourthPanel: {
      show: false,
    },
  }));

  // Reset the sidepanel children after it's been hidden
  setTimeout(() => {
    setPanel({
      children: null,
      extraPanel: {
        children: null,
      },
      thirdPanel: {
        children: null,
      },
    });
  }, 900);
}

/**
 * Save all tokens to cookies
 */
export function saveTokens({
  idToken,
  accessToken,
  refreshToken,
}) {
  const idPayload = jwt.decode(idToken);
  const accessPayload = jwt.decode(accessToken);

  if (idPayload && accessPayload) {
    const idTokenExpiresAt = moment.unix(idPayload.exp);
    const accessTokenExpiresAt = moment.unix(accessPayload.exp);

    const idExp = idTokenExpiresAt
      .add(1, 'week')
      .format();

    const accessExp = accessTokenExpiresAt
      .add(1, 'week')
      .format();

    setLocal('app:user:idToken', idToken, idExp);
    setLocal('app:user:accessToken', accessToken, accessExp);

    if (refreshToken) {
      const refreshExpireDate = new moment()
        .add(1, 'month')
        .format();

      setLocal('app:user:refreshToken', refreshToken, refreshExpireDate);
    }
  }
}

/**
 * Refresh the hosted content cookies (CloudFront)
 */
export async function refreshHostedContent({
  idToken,
}) {
  const response = await request
    .get(`${process.env.VAPI_BASE_URL}/hostedcontent`)
    .set('Authorization', `Bearer ${idToken}`)
    .set('Access-Control-Allow-Origin', window.location.origin)
    .set('Access-Control-Allow-Methods', 'GET')
    .set('Content-Type', 'application/json')
    .set('VAPI-Client', clientHeader);

  const cookies = response.body || {};

  const data = {
    keyPairId: cookies['CloudFront-Key-Pair-Id'],
    policy: cookies['CloudFront-Policy'],
    signature: cookies['CloudFront-Signature'],
    expiresAt: moment(cookies.ExpiresAt),
  };

  // When the storage items actually expire
  const expireDate = data.expiresAt.format();

  const cloudfrontCookies = {
    Policy: data.policy,
    Signature: data.signature,
    'Key-Pair-Id': data.keyPairId,
  };

  setLocal('CloudFront-Policy', data.policy, expireDate);
  setLocal('CloudFront-Signature', data.signature, expireDate);
  setLocal('CloudFront-Key-Pair-Id', data.keyPairId, expireDate);

  // Add these Parameters to Media URLs
  setLocal('app:media:params', queryString.stringify(cloudfrontCookies));
}

/**
 * Check the user session and fetch new tokens if needed
 */
export async function checkSession() {
  const isValid = isValidSession();

  if (isValid) {
    // session is valid and cookies/tokens do not need to be updated
    return;
  }

  const refreshToken = getLocal('app:user:refreshToken');

  if (!refreshToken) {
    // cannot refresh a session without a refresh token
    console.warn('checkSession(): Cannot refresh without a refresh token');
    logout();
    return;
  }

  const authData = {
    authType: 'refreshToken',
    authParams: {
      refreshToken,
    },
  };

  try {
    const response = await authenticate(authData);
    const success = get(response, '[0].type') === 'AUTHENTICATION.AUTHENTICATED';

    if (success) {
      const {
        accessToken,
        idToken,
      } = get(response, '[0].data', {});

      saveTokens({
        idToken,
        accessToken,
      });

      // refresh CloudFront cookies
      await refreshHostedContent({
        idToken,
      });

      console.warn('checkSession(): Tokens Refreshed', response);
    } else {
      console.error('checkSession(): Auth failed', response);
      logout();
    }
  } catch (err) {
    console.error('checkSession(): Auth failed, redirecting to login page', err);
    logout();
  }
}

/**
 * Get the current user session
 */
export async function getSession() {
  await checkSession();

  const idToken = getLocal('app:user:idToken');
  const accessToken = getLocal('app:user:accessToken');
  const refreshToken = getLocal('app:user:refreshToken');

  if (!idToken && !accessToken && !refreshToken) {
    console.warn('There is no active user session');
    return {};
  }

  return {
    idToken,
    accessToken,
    refreshToken,
  };
}
